import { useCallback, useState } from "react";

import { Edit, Trash } from "components/icons/generated";
import ListEntitiesItemDescription from "components/ListEntitiesItem/Description";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import TextEllipsis from "ds/components/TextEllipsis";
import Typography from "ds/components/Typography";

import ContextMountedFilesEditForm from "../ContextMountedFiles/EditForm";
import { ContextAttachedMountedFile } from "../types";
import styles from "./styles.module.css";

type NewContextMountedFileProps = {
  item: ContextAttachedMountedFile;
  onSave?: (index: number, item: ContextAttachedMountedFile) => void;
  onRemove?: (index: number) => void;
  index: number;
};

const NewContextMountedFile = ({ item, onSave, onRemove, index }: NewContextMountedFileProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const canEdit = !!onSave;

  const handleRemove = useCallback(() => onRemove?.(index), [index, onRemove]);

  const handleSave = useCallback(
    (item: ContextAttachedMountedFile) => {
      onSave?.(index, item);
      setIsEditing(false);
    },
    [index, onSave]
  );

  const handleEdit = useCallback(() => setIsEditing(true), []);

  if (isEditing) {
    return <ContextMountedFilesEditForm index={index} onSave={handleSave} />;
  }

  return (
    <Box
      align="center"
      gap="large"
      className={styles.item}
      padding="large"
      justify="between"
      fullWidth
    >
      <Box direction="column" zeroMinWidth>
        <TextEllipsis tooltip={item.path} tooltipWidthMode="maxWidthSm">
          {(props) => (
            <Typography {...props} variant="p-t7" tag="span">
              {item.path}
            </Typography>
          )}
        </TextEllipsis>
        <ListEntitiesItemDescription description={item.description} />
      </Box>
      {canEdit && (
        <Box shrink="0" gap="large">
          <ButtonIcon icon={Edit} onPress={handleEdit} variant="ghost">
            Edit
          </ButtonIcon>
          <ButtonIcon icon={Trash} onPress={handleRemove} variant="ghostDanger">
            Delete
          </ButtonIcon>
        </Box>
      )}
    </Box>
  );
};

export default NewContextMountedFile;
